/**
 * Secret Key 관리 Composable
 * VueUse useSessionStorage 기반 키 관리 및 API 요청 처리
 */

import { useSessionStorage } from '@vueuse/core'

interface SecretKeyData {
  key: string
  expiredAt: number
}

const EXPIRY_TIME = 1000 * 60 * 5 // 5분

// 키 발급 중복 방지를 위한 전역 상태
let isRequestingSecretKey = false
let pendingSecretKeyPromise: Promise<string | null> | null = null

// VueUse useSessionStorage 사용
const SECRET_KEY_STORAGE_KEY = 'secret_key'

// 시리얼라이저 함수들
const serializer = {
  read: (value: string): SecretKeyData | null => {
    try {
      return JSON.parse(value)
    } catch {
      return null
    }
  },
  write: (value: SecretKeyData | null): string => {
    return JSON.stringify(value)
  }
}

/**
 * Secret Key 반응성 변수 반환 (전역 공유)
 */
export const useSecretKey = () => {
  const secretKeyData = useSessionStorage<SecretKeyData | null>(
    SECRET_KEY_STORAGE_KEY, 
    null,
    {
      serializer
    }
  )
  return secretKeyData
}

/**
 * Secret Key 관련 액션 함수들
 */
export const useSecretKeyActions = () => {
  const secretKeyData = useSecretKey()

  /**
   * 현재 키가 유효한지 확인
   */
  const isKeyValid = (): boolean => {
    if (typeof window === 'undefined') {
      return false
    }
    
    if (!secretKeyData.value) {
      return false
    }
    
    const now = Date.now()
    const expiredAt = secretKeyData.value.expiredAt
    const isValid = now <= expiredAt
    
    // 만료된 키가 있으면 자동으로 정리
    if (!isValid && secretKeyData.value) {
      secretKeyData.value = null
    }
    
    return isValid
  }

  /**
   * 세션 스토리지에 키 저장
   */
  const storeKey = (key: string): void => {
    if (typeof window === 'undefined') {
      console.warn('브라우저 환경이 아니므로 세션 스토리지에 저장할 수 없습니다.')
      return
    }

    const now = Date.now()
    const expiredAt = now + EXPIRY_TIME
    const keyData = {
      key,
      expiredAt
    }
    
    try {
      // VueUse useSessionStorage를 통해 저장
      secretKeyData.value = keyData
      
      // 저장 검증
      if (!(secretKeyData.value && secretKeyData.value.key === key)) {
        console.error('❌ [storeKey] 세션 스토리지 저장 실패 - 저장된 값이 다름')
      }
    } catch (error) {
      console.error('❌ [storeKey] 세션 스토리지 저장 중 오류:', error)
    }
  }

  /**
   * Secret Key API 요청 (중복 요청 방지)
   */
  const requestSecretKey = async (): Promise<string | null> => {
    // 이미 요청 중인 경우 기존 Promise 반환
    if (isRequestingSecretKey && pendingSecretKeyPromise) {
      return await pendingSecretKeyPromise
    }
    
    // 새로운 요청 시작
    isRequestingSecretKey = true
    
    const requestPromise = (async (): Promise<string | null> => {
      try {
        const { generateRandomHash, generateSecretKey } = await import('~/utils/crypto.util')
        const hash = generateRandomHash()

        // Vue Query 훅 대신 직접 API 함수 호출
        const { requestSecretKeyApi } = await import('~/api/service/secret.service')
        const response = await requestSecretKeyApi({ hash })

        // SECRET을 디코딩하여 targetValue 확인 (아직 인덱스 아님!)
        let targetValue = null
        try {
          const decodedSecret = atob(response.SECRET)
          targetValue = parseInt(decodedSecret, 10)
        } catch (error) {
          console.error('❌ [requestSecretKey] SECRET 디코딩 실패:', error)
        }

        // API 응답을 generateSecretKey 함수 형식에 맞게 변환
        const convertedInput = {
          s: response.SECRET,
          d: response.DATALIST
        }

        const generatedKey = generateSecretKey(convertedInput)
        
        if (!generatedKey) {
          console.error('❌ [requestSecretKey] Secret Key 생성 실패')
          return null
        }

        // 키 저장
        storeKey(generatedKey)
        
        // 저장 검증
        if (!isKeyValid()) {
          console.error('❌ [requestSecretKey] 키 저장 후 검증 실패')
        }

        return generatedKey
      } catch (error) {
        console.error('❌ [requestSecretKey] Secret Key API 요청 실패:', error)
        return null
      } finally {
        // 요청 완료 후 상태 초기화
        isRequestingSecretKey = false
        pendingSecretKeyPromise = null
      }
    })()
    
    // 현재 요청을 전역 상태에 저장
    pendingSecretKeyPromise = requestPromise
    
    return await requestPromise
  }

  /**
   * 현재 유효한 키 조회 (없으면 자동 발급)
   * 
   * @param forceRefresh 강제 갱신 여부 (기본값: false)
   * @returns Promise<string | null>
   */
  const getCurrentKey = async (forceRefresh = false): Promise<string | null> => {
    // 강제 갱신이 아니고 유효한 키가 있으면 반환
    if (!forceRefresh && isKeyValid()) {
      return secretKeyData.value!.key
    }

    // 키가 없거나 만료된 경우 새로 발급
    const newKey = await requestSecretKey()
    
    return newKey
  }

  /**
   * API 요청용 키 조회 (유효한 키만 반환, 만료된 키는 자동 재갱신)
   * 만료된 키 사용을 방지하기 위한 안전한 함수
   */
  const getKeyForApiRequest = async (): Promise<string | null> => {
    // 1. 유효한 키가 있으면 즉시 반환
    if (isKeyValid()) {
      return secretKeyData.value!.key
    }

    // 2. 키 발급 요청 중이면 대기
    if (isRequestingSecretKey && pendingSecretKeyPromise) {
      return await pendingSecretKeyPromise
    }

    // 3. 키가 없거나 만료된 경우 새로 발급 시도 (중복 요청 방지 적용)
    if (!isRequestingSecretKey) {
      const newKey = await requestSecretKey()
      
      if (newKey) {
        return newKey
      }
    }
    
    return null
  }

  /**
   * 초기화 함수. 첫 진입 시 세션 스토리지에 키가 없으면 새로 발급받는다.
   * 클라이언트 사이드에서만 실행되어야 한다.
   */
  const initializeSecretKey = async (): Promise<void> => {
    if (typeof window === 'undefined') {
      return
    }

    if (isKeyValid()) {
      return
    }

    const newKey = await requestSecretKey()
    
    if (!newKey) {
      console.error('❌ [initializeSecretKey] 초기화 실패 - 키 발급 실패')
    }
  }

  return {
    isKeyValid,
    getCurrentKey,
    getKeyForApiRequest,
    requestSecretKey,
    initializeSecretKey
  }
}
